import defu from 'defu'
import {defineStore} from 'pinia'
import {TrashBinType} from '~/types/enums'
import {
  AllWaste,
  AnonymousAllCityRanking,
  AnonymousCityRanking,
  AnonymousCitySortRatio,
  CitySortRatio,
  FeePayer,
  PaginationQueryParams,
  PeriodFilter,
  SortRatio,
  StreetSortRatio,
  Subject,
  TopWaste,
  TrashBin,
  TrashBins,
  UnsortableWaste,
  WasteCard,
  WasteCollectionRoute,
  WasteCollectionRouteOverview,
  WasteCollectionSchedule,
  WasteCollectionScheduleOverview,
  WasteCollectionYard,
  WasteCollectionYardVisit,
  WasteCollectionYardVisits,
  WasteCompanyInfo,
  WasteEvidence,
  WasteEvidenceOverview,
  WasteEvidences,
  WasteLimit,
} from '~/types/types'
import {useApi} from '~/composables/useApi'

export const useSubjectStore = defineStore('subject', () => {
    const {$moment} = useNuxtApp()
    const {$formatPercentage} = useNuxtApp()

    const userStore = useUserStore()

    const subject = ref<Subject>(null)
    const wasteCard = ref<WasteCard>(null)
    const unsortableWaste = ref<UnsortableWaste>(null)
    const topWastes = ref<TopWaste[]>([])
    const allWastes = ref<AllWaste[]>([])
    const sortRatio = ref<SortRatio>(null)
    const citySortRatio = ref<CitySortRatio>(null)
    const anonymousCitySortRatio = ref<AnonymousCitySortRatio>(null)
    const anonymousCityRanking = ref<AnonymousCityRanking>(null)
    const anonymousAllCityRanking = ref<AnonymousAllCityRanking>(null)
    const streetSortRatio = ref<StreetSortRatio>(null)
    const wasteEvidences = ref<WasteEvidences>({items: []})
    const wasteCollectionYard = ref<WasteCollectionYard>(null)
    const wasteCollectionYards = ref(null)
    const wasteCompanyInfo = ref<WasteCompanyInfo>(null)
    const motivationInfo = ref<any>(null)
    const wasteEvidenceOverview = ref<WasteEvidenceOverview[]>([])
    const wasteCollectionSchedule = ref<WasteCollectionSchedule[]>([])
    const wasteCollectionScheduleOverview = ref<WasteCollectionScheduleOverview[]>([])
    const wasteCollectionRoute = ref<WasteCollectionRoute[]>([])
    const wasteCollectionRouteOverview = ref<WasteCollectionRouteOverview[]>([])
    const wasteLimit = ref<WasteLimit[]>([])
    const topWasteCollectionYardVisits = ref<WasteCollectionYardVisit[]>([])
    const wasteCollectionYardVisits = ref<WasteCollectionYardVisits>({items: []})
    const feePayers = ref<FeePayer[]>([])
    const topTrashBins = ref<TrashBin[]>([])
    const trashBins = ref<TrashBins>({items: []})
    const motivationType = ref<any>(null)
    const feeYearlyView = ref([])
    const feeMonthlyViews = ref([])

    const fetchSubject = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}`)

      if (!response.error.value) {
        const data: any = response.data.value

        subject.value = {
          id: data.id,
          name: data.name,
          streetAddress: data.streetAddress,
          municipalityName: data.municipalityName,
          postCode: data.postCode,
          location: data.location
            ? {
              latitude: data.location.latitude,
              longitude: data.location.longitude,
            }
            : null,
          subjectType: data.subjectType,
          feePayerCount: data.feePayerCount,
          unsortableWasteWeight: data.unsortableWasteWeight,
          otherWasteWeight: data.otherWasteWeight,
          topWastes: data.topWastes.map((topWaste: any) => ({
            wasteName: topWaste.wasteName,
            wasteColor: isEmpty(topWaste.wasteColor) ? '#000000' : topWaste.wasteColor,
            weight: topWaste.weight,
          })),
          sortRatio: data.sortRatio,
          otherSubjectsPercentil: data.otherSubjectsPercentil,
        }
      }

      return response
    }

    const fetchWasteCard = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/waste-card`)

      if (!response.error.value && response.data.value) {
        const data: any = response.data.value

        wasteCard.value = {
          id: data.id,
          code: data.code,
          plainCode: data.plainCode,
          feePayerName: data.feePayerName,
          state: data.state,
          validFrom: data.validFrom ? $moment(data.validFrom) : null,
          validTo: data.validTo ? $moment(data.validTo) : null,
        }
      }

      return response
    }

    const changeDefaultWasteCard = async (id: number, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      return await useApi(`/subject/${subjectId}/waste-card/${id}`, {
        method: 'PATCH',
      })
    }

    const fetchTopWastes = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/top-waste`, {
        params:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value

        unsortableWaste.value = {
          weight: data.unsortableWasteWeight,
          trend: Number((data.unsortableWasteTrend * 100).toFixed(2)),
          isWasteCollectionYardWaste: data.isWasteCollectionYardWaste,
        }

        topWastes.value = data
      }

      return response
    }

    const fetchAllWastes = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      const response = await useApi(`/subject/${subjectId}/all-waste`, {
        params:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value

        allWastes.value = data.items.map((allWaste: any) => {
          return {
            code: allWaste.wasteCode,
            name: allWaste.wasteName,
            color: isEmpty(allWaste.wasteColor) ? '#000000' : allWaste.wasteColor,
            weight: allWaste.weight,
            previousWeight: allWaste.previousWeight,
            trend: allWaste.trend
          }
        })
      }

      return response
    }

    const fetchSortRatio = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/sort-ratio`, {
        params:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value

        const wastes = data.topWastes.map((topWaste: any) => ({
          color: topWaste.wasteColor,
          name: topWaste.wasteName,
          weight: topWaste.weight,
        }))

        sortRatio.value = {
          municipality: data.municipalityName,
          otherSubjectsPercentil: $formatPercentage(data.otherSubjectsPercentil),
          otherWasteWeight: data.otherWasteWeight,
          unsortableWasteWeight: data.unsortableWasteWeight,
          sortRatio: $formatPercentage(data.sortRatio),
          trend: Number((data.trend * 100).toFixed(2)),
          topWastes: wastes,
        }
      }

      return response
    }

    const fetchCitySortRatio = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/waste-compare/${subjectId}/city-compare`, {
        params:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value

        citySortRatio.value = {
          city: data.city,
          country: data.country,
          currentSortRatio: $formatPercentage(data.currentSortRatio),
          otherWasteWeight: data.otherWasteWeight,
          previousSortRatio: $formatPercentage(data.previousSortRatio),
          sortRatioTrend: $formatPercentage(data.sortRatioTrend),
          street: data.street,
          topSortableWastes: data.topSortableWastes,
          topUnsortableWastes: data.topUnsortableWastes,
        }
      }

      return response
    }

    const fetchAnonymousCitySortRatio = async (city: string | null = null, period: PeriodFilter | null = null) => {
      const response = await useAnonymousApi('/waste-compare/city-compare', {
        query: defu(
          city ? {city: city} : {},
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {}
        ),
      })

      if (!response.error.value) {
        const data: any = response.data.value

        anonymousCitySortRatio.value = {
          from: data.from ? $moment(data.from) : null,
          to: data.to ? $moment(data.to) : null,
          city: data.city
            ? {
              street: data.city.street,
              city: data.city.city,
              country: data.city.country,
              unsortableWasteWeight: data.city.unsortableWasteWeight,
              otherWasteWeight: data.city.otherWasteWeight,
              currentSortRatio: $formatPercentage(data.city.currentSortRatio),
              previousSortRatio: $formatPercentage(data.city.previousSortRatio),
              sortRatioTrend: $formatPercentage(data.city.sortRatioTrend),
              topWastes: data.city.topWastes.map((topWaste: any) => ({
                wasteCode: topWaste.wasteCode,
                wasteName: topWaste.wasteName,
                wasteColor: isEmpty(topWaste.wasteColor) ? '#000000' : topWaste.wasteColor,
                isSortable: topWaste.isSortable,
                weight: topWaste.weight,
              })),
            }
            : null,
          country: data.country
            ? {
              street: data.country.street,
              city: data.country.country,
              country: data.country.country,
              unsortableWasteWeight: data.country.unsortableWasteWeight,
              otherWasteWeight: data.country.otherWasteWeight,
              currentSortRatio: $formatPercentage(data.country.currentSortRatio),
              previousSortRatio: $formatPercentage(data.country.previousSortRatio),
              sortRatioTrend: $formatPercentage(data.country.sortRatioTrend),
              topWastes: data.country.topWastes.map((topWaste: any) => ({
                wasteCode: topWaste.wasteCode,
                wasteName: topWaste.wasteName,
                wasteColor: isEmpty(topWaste.wasteColor) ? '#000000' : topWaste.wasteColor,
                isSortable: topWaste.isSortable,
                weight: topWaste.weight,
              })),
            }
            : null,
        }
      }

      return response
    }

    const fetchAnonymousCityRanking = async (city: string | null = null, period: PeriodFilter | null = null) => {
      const response = await useAnonymousApi('/waste-compare/city-ranking', {
        query: defu(
          city ? {city: city} : {},
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {}
        ),
      })

      if (!response.error.value) {
        const data: any = response.data.value

        anonymousCityRanking.value = {
          city: data.city,
          ranking: data.ranking,
          citiesCount: data.citiesCount,
          allCitiesPercentile: Number((data.allCitiesPercentile * 100).toFixed(2)),
          country: data.country,
          citiesRanking: data.citiesRanking.map((ranking: any) => ({
            city: ranking.city,
            country: ranking.country,
            sortRatio: $formatPercentage(ranking.sortRatio),
            rank: ranking.rank,
          })),
        }
      }

      return response
    }

    const fetchAnonymousAllCityRanking = async (period: PeriodFilter | null = null) => {
      const response = await useAnonymousApi('/waste-compare/all-city-ranking', {
        query:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value

        anonymousAllCityRanking.value = {
          total: data.total,
          items: data.items.map((item: any) => ({
            city: item.city,
            country: item.country,
            sortRatio: $formatPercentage(item.sortRatio),
            rank: item.rank,
          })),
        }
      }

      return response
    }

    const fetchStreetSortRatio = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/waste-compare/${subjectId}/street-compare`, {
        params:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value
        streetSortRatio.value = {
          city: data.city,
          country: data.country,
          currentSortRatio: $formatPercentage(data.currentSortRatio),
          otherWasteWeight: data.otherWasteWeight,
          previousSortRatio: $formatPercentage(data.previousSortRatio),
          sortRatioTrend: $formatPercentage(data.sortRatioTrend),
          street: data.street,
          topSortableWastes: data.topSortableWastes,
          topUnsortableWastes: data.topUnsortableWastes,
        }
      }

      return response
    }

    const fetchWasteCollectionYard = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/waste-collection-yard-info`)

      const data: any = response.data.value
      if (!response.error.value && data.items.length > 0) {
        wasteCollectionYards.value = data.items
        wasteCollectionYard.value = data.items.find(i => i.isFavourite) ?? data.items[0]
      }
      return response
    }

    const fetchPaymentInfo = async (params: { subjectPaymentReportId: number }) => {
      const response = await useApi(`/subject/${params.subjectPaymentReportId}/payment-info`)

      const data: any = response.data.value

      if (!response.error.value && data.items?.length > 0) {
        const firstWasteCollectionYard = data.items[0]

        wasteCollectionYard.value = {
          id: firstWasteCollectionYard.id,
          name: firstWasteCollectionYard.name,
          street: firstWasteCollectionYard.streetAddress,
          location: firstWasteCollectionYard.location,
          openingSchedule: firstWasteCollectionYard.openingSchedule,
        }
      }

      return response
    }

    const fetchMotivationInfo = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/motivation-info`)

      if (!response.error.value) {
        const data: any = response.data.value?.data

        if (data.items.length > 0) {
          motivationInfo.value = {
            id: data.items[0]?.id,
            name: data.items[0]?.name ?? '-',
            description: data.items[0]?.description,
            type: data.items[0]?.type,
          }
        }
        motivationType.value = response.data.value?.useVolumeAsUnits
      }

      return response
    }

    /*const fetchWasteCompanyInfo = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/waste-company-info`)

      if (!response.error.value) {
        const data: any = response.data.value

        wasteCompanyInfo.value = {
          id: data.items[0]?.id,
          name: data.items[0]?.name ?? '-',
          logoUrl: data.items[0]?.logoUrl,
        }
      }

      return response
    }*/

    const fetchWasteEvidences = async (
      search: string | undefined = undefined,
      period: PeriodFilter | null = null,
      queryParams: PaginationQueryParams | null = null,
      subjectId: number | null
    ) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-evidence`, {
        query: defu(
          search ? {SearchText: search} : {},
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
          queryParams?.page ? {Page: queryParams.page} : {},
          queryParams?.orderBy.column ? {OrderByCol: queryParams.orderBy.column} : {},
          queryParams?.orderBy.direction ? {OrderByDirection: queryParams.orderBy.direction} : {}
        ),
      })

      if (!response.error.value) {
        const data: any = response.data.value

        const items = data.items.map((item: WasteEvidence) => ({
          id: item.id,
          processedTs: $moment(item.processedTs),
          wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
          binName: item.binName,
          barcode: item.barcode,
          weight: item.weight,
          approximationCoefficient: item.approximationCoefficient,
          state: item.state,
          isWasteCollectionYardWaste: item.isWasteCollectionYardWaste,
          volume: item.volume,
        }))

        wasteEvidences.value = {
          items,
          pagination: {
            page: data.page,
            pageCount: data.pageCount,
            limit: data.limit,
            total: data.total,
          },
        }
      }

      return response
    }

    const fetchTotalFee = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      if (!subjectId) return
      const response = await useApi(`/subject/${subjectId}/total-fee`, {
        query: defu(
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {}
        ),
      })

      if (!response.error.value) {
        return response.data.value
      }
    }

    const fetchWasteEvidenceOverview = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-evidence/overview`)

      if (!response.error.value) {
        const data: any = response.data.value

        wasteEvidenceOverview.value = data.map(item => ({
          id: item.id,
          binName: item.binName,
          barcode: item.barcode,
          approximationCoefficient: item.approximationCoefficient,
          wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
          weight: item.weight,
          volume: item.volume,
          state: item.state,
          processedAt: $moment(item.processedTs),
          isWasteCollectionYardWaste: item.isWasteCollectionYardWaste,
        }))
      }

      return response
    }

    const fetchWasteCollectionSchedule = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-collection-schedule`)

      if (!response.error.value) {
        const data: any = response.data.value

        wasteCollectionSchedule.value = data.items.map(item => ({
          id: item.wasteCollectionRouteId,
          wasteName: item.wasteName,
          wasteColor: item.wasteColor,
          collectionDate: $moment(item.collectionDateTs),
        }))
      }

      return response
    }

    const fetchWasteCollectionScheduleOverview = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-collection-schedule/overview`)

      if (!response.error.value) {
        const data: any = response.data.value

        wasteCollectionScheduleOverview.value = data.map(item => ({
          id: item.wasteCollectionRouteId,
          wasteName: item.wasteName,
          wasteColor: item.wasteColor,
          collectionDate: $moment(item.collectionDateTs),
        }))
      }

      return response
    }

    const fetchWasteCollectionRoute = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-collection-route`)

      if (!response.error.value) {
        const data: any = response.data.value

        wasteCollectionRoute.value = data.items.map(item => ({
          id: item.wasteCollectionRouteId,
          wasteName: item.wasteName,
          wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
          collectionDayOfWeek: item.collectionDayOfWeek,
          collectionRecurrenceUnit: item.collectionRecurrenceUnit,
          collectionRecurrenceValue: item.collectionRecurrenceValue,
          nextCollectionDate: $moment(item.nextCollectionDateTs),
          isNotificationEnabled: item.isNotificationEnabled,
        }))
      }

      return response
    }

    const fetchWasteCollectionRouteOverview = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-collection-route/overview`)

      if (!response.error.value) {
        const data: any = response.data.value

        wasteCollectionRouteOverview.value = data.map(item => ({
          id: item.wasteCollectionRouteId,
          wasteName: item.wasteName,
          wasteColor: item.wasteColor,
          collectionDayOfWeek: item.collectionDayOfWeek,
          collectionRecurrenceUnit: item.collectionRecurrenceUnit,
          collectionRecurrenceValue: item.collectionRecurrenceValue,
          nextCollectionDate: $moment(item.nextCollectionDateTs),
          isNotificationEnabled: item.isNotificationEnabled,
        }))
      }

      return response
    }

    const toggleWasteCollectionRouteNotification = async (
      wasteCollectionRouteId: number,
      enabled: boolean,
      subjectId: number | null
    ) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const data = await useApi(`/subject/${subjectId}/waste-collection-route/${wasteCollectionRouteId}`, {
        method: 'PATCH',
        query: {
          enableNotification: enabled,
        },
      })

      try {
        wasteCollectionRouteOverview.value.find(route => route.id === wasteCollectionRouteId).isNotificationEnabled =
          enabled
      } catch (error) {
      }

      try {
        wasteCollectionRoute.value.find(route => route.id === wasteCollectionRouteId).isNotificationEnabled = enabled
      } catch (error) {
      }

      return data
    }

    const submitSuggestion = async (type: string, text: string | null = null, binId: string | null = null) => {
      const response = await useApi(`/user/suggestion`, {
        method: 'POST',
        body: defu({suggestionType: type}, text ? {suggestionText: text} : {}, binId ? {binId: binId} : {}),
      })

      return response
    }

    const fetchWasteLimit = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/waste-limits/${wasteCollectionYard.value.id}`, {
        params:
          period && period.from && period.to
            ? {
              'Date.From': period.from,
              'Date.To': period.to,
            }
            : {},
      })

      if (!response.error.value) {
        const data: any = response.data.value

        const items = data.items.map(item => ({
          wasteId: item.wasteId,
          wasteName: item.wasteName,
          wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
          wasteLimit: item.wasteLimit,
          used: item.used,
          usedPercentage: (item.used / item.wasteLimit) * 100,
          remaining: item.remaining,
        }))

        wasteLimit.value = useSortBy(items, [item => item.remaining])
      }

      return response
    }

    const fetchTopWasteCollectionYardVisits = async (period: PeriodFilter | null = null, subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(
        `/subject/${subjectId}/waste-collection-yard-report/${wasteCollectionYard.value.id}/overview`,
        {
          params:
            period && period.from && period.to
              ? {
                'Date.From': period.from,
                'Date.To': period.to,
              }
              : {},
        }
      )

      if (!response.error.value) {
        const data: any = response.data.value

        topWasteCollectionYardVisits.value = data.items.map(item => ({
          id: item.id,
          invoice: item.invoice,
          fee: item.totalFee,
          feePaid: item.feePaid,
          startReportTs: $moment(item?.startReportTs),
          endReportTs: $moment(item?.endReportTs),
          feePayerName: item.feePayerName,
          feePayerWasteCardCode: item.feePayerWasteCardCode,
          wasteName: item.wasteName,
          wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
          weight: item.totalWeight,
          recordCount: item.recordCount,
          subRecords: null,
        }))
      }

      for await (const visit of topWasteCollectionYardVisits.value) {
        if (visit.recordCount <= 1) {
          continue
        }

        const response = await useApi(`/waste-collection-yard-report/${visit.id}/records`)

        if (!response.error.value) {
          const data: any = response.data.value
          const idx: number = topWasteCollectionYardVisits.value.findIndex(v => v.id === visit.id)

          topWasteCollectionYardVisits.value[idx].subRecords = data.items.map(item => ({
            startReportTs: item.recordedTs ? $moment(item.recordedTs) : null,
            wasteName: item.wasteName,
            wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
            weight: item.weight,
            fee: item.fee,
          }))
        }
      }

      return response
    }

    const fetchWasteCollectionYardVisits = async (
      period: PeriodFilter | null = null,
      search: string | undefined = undefined,
      queryParams: PaginationQueryParams | null = null,
      subjectId: number | null
    ) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(
        `/subject/${subjectId}/waste-collection-yard-report/${wasteCollectionYard.value.id}`,
        {
          query: defu(
            search ? {SearchText: search} : {},
            period && period.from && period.to
              ? {
                'Date.From': period.from,
                'Date.To': period.to,
              }
              : {},
            queryParams?.page ? {Page: queryParams.page} : {},
            queryParams?.orderBy?.column ? {OrderByCol: queryParams.orderBy.column} : {},
            queryParams?.orderBy?.direction ? {OrderByDirection: queryParams.orderBy.direction} : {}
          ),
        }
      )

      if (!response.error.value) {
        const data: any = response.data.value

        const items = data.items.map(item => ({
          id: item.id,
          invoice: item.invoice,
          fee: item.totalFee,
          feePaid: item.feePaid,
          startReportTs: item.startReportTs ? $moment(item.startReportTs) : null,
          endReportTs: item.endReportTs ? $moment(item.endReportTs) : null,
          feePayerName: item.feePayerName,
          feePayerWasteCardCode: item.feePayerWasteCardCode,
          wasteName: item.wasteName,
          wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
          weight: item.totalWeight,
          recordCount: item.recordCount,
          subRecords: null,
        }))

        wasteCollectionYardVisits.value = {
          items,
          pagination: {
            page: data.page,
            pageCount: data.pageCount,
            limit: data.limit,
            total: data.total,
          },
        }
      }

      for await (const visit of wasteCollectionYardVisits.value.items) {
        if (visit.recordCount <= 1) {
          continue
        }

        const response = await useApi(`/waste-collection-yard-report/${visit.id}/records`)

        if (!response.error.value) {
          const data: any = response.data.value
          const idx: number = wasteCollectionYardVisits.value.items.findIndex(v => v.id === visit.id)

          wasteCollectionYardVisits.value.items[idx].subRecords = data.items.map(item => ({
            startReportTs: item.recordedTs ? $moment(item.recordedTs) : null,
            wasteName: item.wasteName,
            wasteColor: isEmpty(item.wasteColor) ? '#000000' : item.wasteColor,
            weight: item.weight,
            fee: item.fee,
          }))
        }
      }

      return response
    }

    const fetchFeePayers = async (subjectId: number | null) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/fee-payer`)

      if (!response.error.value) {
        const data: any = response.data.value

        feePayers.value = data.items.map((feePayer: any) => ({
          ...feePayer,
          dateOfBirth: feePayer.dateOfBirth ? $moment(feePayer.dateOfBirth) : null,
          activeFrom: feePayer.activeFrom ? $moment(feePayer.activeFrom) : null,
          activeTo: feePayer.activeTo ? $moment(feePayer.activeTo) : null,
          activeChangedTs: feePayer.activeChangedTs ? $moment(feePayer.activeChangedTs) : null,
        }))
      }

      return response
    }

    const updateFeePayer = async (
      data: { id: number; email: string | null; phone: string | null },
      subjectId: number | null
    ) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/fee-payer/${data.id}`, {
        method: 'PUT',
        body: {
          email: data.email,
          phone: data.phone,
        },
      })

      return response
    }
    /**
     * @typedef {Object} TrashBinType
     * @property {boolean} ShowDeactivatedBins
     * @property {boolean} ShowFixedBins
     * @property {boolean} ShowDisposableBins
     */

    /**
     * Fetches the top trash bins.
     * @param {TrashBinType} trashBinType - The type of trash bins to fetch.
     * @param {number | null} subjectId - The ID of the subject.
     * @returns {Promise<any>} The response from the API.
     */
    const fetchTopTrashBins = async (trashBinType, subjectId) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/trash-bin/overview`, {
        params: trashBinType,
      })

      if (!response.error.value) {
        const data: any = response.data.value

        topTrashBins.value = data.items.map((trashBin: any) => ({
          id: trashBin.id,
          binName: trashBin.binName,
          wasteName: trashBin.wasteName,
          wasteColor: isEmpty(trashBin.wasteColor) ? '#000000' : trashBin.wasteColor,
          state: trashBin.state,
          assignedTs: trashBin.assignedTs ? $moment(trashBin.assignedTs) : null,
          barcode: trashBin.barcode,
          epc: trashBin.epc,
          totalWasteCollected: trashBin.totalWasteCollected,
        }))
      }

      return response
    }

    const fetchTrashBins = async (
      TrashBinType: object,
      search: string | null = null,
      queryParams: PaginationQueryParams | null = null,
      subjectId: number | null
    ) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/trash-bin`, {
        query: defu(
          search ? {SearchText: search} : {},
          queryParams?.page ? {Page: queryParams.page} : {},
          queryParams?.orderBy?.column ? {OrderByCol: queryParams.orderBy.column} : {},
          queryParams?.orderBy?.direction ? {OrderByDirection: queryParams.orderBy.direction} : {}
        ),
        params: TrashBinType,
      })

      if (!response.error.value) {
        const data: any = response.data.value

        const items = data.items.map((trashBin: any) => ({
          id: trashBin.id,
          binName: trashBin.binName,
          wasteName: trashBin.wasteName,
          wasteColor: isEmpty(trashBin.wasteColor) ? '#000000' : trashBin.wasteColor,
          assignedTs: trashBin.assignedTs ? $moment(trashBin.assignedTs) : null,
          state: trashBin.state,
          barcode: trashBin.barcode,
          epc: trashBin.epc,
          totalWasteCollected: trashBin.totalWasteCollected,
        }))

        trashBins.value = {
          items,
          pagination: {
            page: data.page,
            pageCount: data.pageCount,
            limit: data.limit,
            total: data.total,
          },
          sorting: [
            {
              desc: queryParams?.orderBy?.direction === 'Descending',
              id: queryParams?.orderBy?.column,
            },
          ],
        }
      }

      return response
    }

    const fetchCalculatorInfo = async (subjectId: number | null) => {
      return useApi(`/subject/${subjectId}/calculator-info`);
    }

    const submitCalculator = async (subjectId: number | null, memberCount: number, bins: object) => {
      return useApi(`/subject/${subjectId}/calculator`, {
        method: 'POST',
        body: defu({memberCount: memberCount, bins: bins}),
      });
    }

    const fetchFeePeriodView = async ({subjectId, params}) => {

      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id
      const response = await useApi(`/subject/${subjectId}/fee-period-view`, {params})

      if (response.error.value) {
        throw new Error('Failed to fetch fee period view')
      }

      feeYearlyView.value[subjectId] = response.data
      return response
    }

    const fetchFeeMonthlyView = async ({subjectId, params}) => {
      if (!subjectId) subjectId = userStore?.activeSubject?.id || userStore.user?.subject?.id

      const response = await useApi(`/subject/${subjectId}/fee-monthly-view`, {params})

      if (response.error.value) {
        throw new Error('Failed to fetch fee period view')
      }

      feeMonthlyViews.value[subjectId] = response.data

      return response
    }

    const assignByMasterCode = async ({subjectId, params}) => {
      const response = await useApi(`/subject/${subjectId}/assign-by-master-code`, {
        method: 'POST',
        params
      })

      if (response.error.value) {
        throw new Error(response.error.value?.Message)
      }

      return response
    }

    const fetchSubjectPaymentReportPdf = async (subjectPaymentReportId: number) => {
      return useApi(`subject/${subjectPaymentReportId}/payment-info`);
    }

    return {
      subject,
      wasteCard,
      unsortableWaste,
      topWastes,
      allWastes,
      sortRatio,
      citySortRatio,
      anonymousCitySortRatio,
      anonymousCityRanking,
      anonymousAllCityRanking,
      streetSortRatio,
      wasteCollectionYards,
      wasteCollectionYard,
      wasteCompanyInfo,
      motivationInfo,
      wasteEvidences,
      wasteEvidenceOverview,
      wasteCollectionSchedule,
      wasteCollectionScheduleOverview,
      wasteCollectionRoute,
      wasteCollectionRouteOverview,
      wasteLimit,
      topWasteCollectionYardVisits,
      wasteCollectionYardVisits,
      feePayers,
      topTrashBins,
      motivationType,
      trashBins,
      feeYearlyView,
      feeMonthlyViews,
      fetchTotalFee,
      fetchSubject,
      fetchWasteCard,
      changeDefaultWasteCard,
      fetchTopWastes,
      fetchAllWastes,
      fetchSortRatio,
      fetchCitySortRatio,
      fetchAnonymousCitySortRatio,
      fetchAnonymousCityRanking,
      fetchAnonymousAllCityRanking,
      fetchStreetSortRatio,
      fetchWasteCollectionYard,
      fetchPaymentInfo,
      fetchMotivationInfo,
      fetchWasteEvidences,
      fetchWasteEvidenceOverview,
      fetchWasteCollectionSchedule,
      fetchWasteCollectionScheduleOverview,
      fetchWasteCollectionRoute,
      fetchWasteCollectionRouteOverview,
      toggleWasteCollectionRouteNotification,
      submitSuggestion,
      fetchWasteLimit,
      fetchTopWasteCollectionYardVisits,
      fetchWasteCollectionYardVisits,
      fetchFeePayers,
      updateFeePayer,
      fetchTopTrashBins,
      fetchTrashBins,
      submitCalculator,
      fetchCalculatorInfo,
      fetchFeePeriodView,
      fetchFeeMonthlyView,
      assignByMasterCode,
      fetchSubjectPaymentReportPdf,
    }
  }
)
